import React, { useEffect, useState } from "react";
import CalibrationApi from "../../../../../api/CalibrationApi";
import {
  CartesianCoordinateHotelConfig,
  Ri360AutoCalibrationState,
  Ri360AutoCalibrationFineTuningResult,
} from "../../../../../Model/Calibration";
import { CartesianCoordinate } from "../../../../../Model/Coordinate";
import { ECarrier } from "../../../../../Model/ECarrier";
import { PsrState } from "../../../../../Model/ModuleState";
import { EFootprint } from "../../../../../Model/PlateType";
import {
  Ri360CarrierCalibration,
  Ri360HotelCalibration,
} from "../../../../../Model/StorageConfig";
import { EArmType } from "../../../../../Model/Waypoint";
import MtiButton from "../../../../Buttons/MtiButton";
import CommonDropDown from "../../../../Common/DropDown/CommonDropDown";
import TextBox from "../../../../Common/TextBox";
import "./AutoCalibrationResultWithFineTuning.css";
import { getCursorOffset } from "../../../../../redux/reducers/PsrCameraLiveImageReducer";
import { useTypedSelector } from "../../../../../redux/reducers/RootReducer";
import { useDispatch } from "react-redux";
import { setCursorOffset } from "../../../../../redux/actions/PsrCameraLiveImageAction";

export enum EMoveButtonType {
  Store,
  Retrieve,
}

export enum EMoveButtonState {
  Idle,
  Loading,
  Disabled,
}

enum ECarrierCorner {
  Unknown,
  Top,
  Bottom,
}

export type MoveButtonsState = Map<EMoveButtonType, EMoveButtonState>;

type Props = {
  psrState: PsrState;
  psrIdentifier: string;
  carrierFineTuningResult: Ri360AutoCalibrationFineTuningResult[];
  setCarrierFineTuningResult: (
    fineTuningResult: Ri360AutoCalibrationFineTuningResult[]
  ) => void;
  storageCalibration: Ri360CarrierCalibration;
  disabled: boolean;
};

export default function AutoCalibrationResultWithFineTuning({
  psrState,
  psrIdentifier,
  carrierFineTuningResult,
  setCarrierFineTuningResult,
  storageCalibration,
  disabled,
}: Props) {
  const dispatch = useDispatch();

  //-------Redux Selector-------//

  const cursorOffset = useTypedSelector((state) => getCursorOffset(state));

  //-------End Redux Selector-------//

  //-------State-------//

  const [selectedCarrierCorner, setSelectedCarrierCorner] = useState(
    ECarrierCorner.Unknown
  );

  const [selectedHotelIndex, setSelectedHotelIndex] = useState(0);

  //-------End State-------//

  //-------Function-------//

  const getOffsetTextBox = (
    key: "topOffset" | "bottomOffset",
    axis: keyof CartesianCoordinate,
    value: number | null
  ) => {
    return (
      <TextBox
        name="auto-cal-result-top"
        variant="number"
        isInteger={false}
        decimalPoint={2}
        value={value ?? 0}
        disabled={disabled || value === null}
        onChange={(value) => {
          if (value != null) {
            const newHotelCarrierFineTuningResult = {
              ...carrierFineTuningResult[selectedHotelIndex],
            };
            newHotelCarrierFineTuningResult[key][axis] = value as number;
            const newCarrierFineTuningResult = [...carrierFineTuningResult];
            newCarrierFineTuningResult[selectedHotelIndex] =
              newHotelCarrierFineTuningResult;

            setCarrierFineTuningResult(newCarrierFineTuningResult);
          }
        }}
      />
    );
  };

  const getTextBox = (
    key: "topUpdated" | "bottomUpdated",
    axis: keyof CartesianCoordinate,
    value: number | null
  ) => {
    return (
      <TextBox
        name="auto-cal-result-top"
        variant="number"
        isInteger={false}
        decimalPoint={2}
        value={value ?? 0}
        disabled={disabled || value === null}
        onChange={(value) => {
          const newHotelCarrierFineTuningResult = {
            ...carrierFineTuningResult[selectedHotelIndex],
          };
          newHotelCarrierFineTuningResult[key][axis] = value as number;
          const newCarrierFineTuningResult = [...carrierFineTuningResult];
          newCarrierFineTuningResult[selectedHotelIndex] =
            newHotelCarrierFineTuningResult;

          setCarrierFineTuningResult(newCarrierFineTuningResult);
        }}
      />
    );
  };

  const onHotelChanged = (hotelIndex: number) => {
    setSelectedHotelIndex(hotelIndex);
  };

  const onClickRow = (carrierCorner: ECarrierCorner) => {
    if (!disabled) {
      setSelectedCarrierCorner(carrierCorner);
    }
  };

  const onClickMove = (carrierCorner: ECarrierCorner) => {
    const defaultVelocity = psrState.cartesianDefaultVelocity ?? {
      x: 0,
      y: 0,
      z: 0,
    };
    let coordinate = { x: 0, y: 0, z: 0 };

    switch (carrierCorner) {
      case ECarrierCorner.Top:
        coordinate = hotelFineTuningResult.topUpdated;
        break;
      case ECarrierCorner.Bottom:
        coordinate = hotelFineTuningResult.bottomUpdated;
    }

    const vectorMove = {
      coordinate: coordinate,
      velocity: defaultVelocity,
      isAbsolute: false,
      name: "Move ",
    };

    CalibrationApi.cartesianArmMoveAbsolute(
      psrIdentifier,
      EArmType.PsrArm,
      vectorMove
    );
  };

  const onClickMoveCursor = (carrierCorner: ECarrierCorner) => {
    if (carrierCorner === ECarrierCorner.Top) {
      console.log(hotelFineTuningResult.topOffset);
      dispatch(
        setCursorOffset({
          x: hotelFineTuningResult.topOffset.y,
          y: hotelFineTuningResult.topOffset.z,
        })
      );
    } else {
      console.log(hotelFineTuningResult.bottomOffset);
      dispatch(
        setCursorOffset({
          x: hotelFineTuningResult.bottomOffset.y,
          y: hotelFineTuningResult.bottomOffset.z,
        })
      );
    }
  };

  const onClickInputPos = (carrierCorner: ECarrierCorner) => {
    let newHotelFineTuningResult = {
      ...carrierFineTuningResult[selectedHotelIndex],
    };

    if (carrierCorner === ECarrierCorner.Top) {
      newHotelFineTuningResult.topUpdated = {
        x: 0,
        y: psrState.cartesianPosition.x,
        z: psrState.cartesianPosition.y,
      };
    } else {
      newHotelFineTuningResult.bottomUpdated = {
        x: 0,
        y: psrState.cartesianPosition.x,
        z: psrState.cartesianPosition.y,
      };
    }

    let newFineTuningResult = [...carrierFineTuningResult];
    newFineTuningResult[selectedHotelIndex] = newHotelFineTuningResult;
    setCarrierFineTuningResult(newFineTuningResult);
  };

  const onClickInputCursorPos = (carrierCorner: ECarrierCorner) => {
    let newHotelFineTuningResult = {
      ...carrierFineTuningResult[selectedHotelIndex],
    };

    if (carrierCorner === ECarrierCorner.Top) {
      newHotelFineTuningResult.topOffset = {
        x: 0,
        y: cursorOffset.x,
        z: cursorOffset.y,
      };
    } else {
      newHotelFineTuningResult.bottomOffset = {
        x: 0,
        y: cursorOffset.x,
        z: cursorOffset.y,
      };
    }

    let newFineTuningResult = [...carrierFineTuningResult];
    newFineTuningResult[selectedHotelIndex] = newHotelFineTuningResult;
    setCarrierFineTuningResult(newFineTuningResult);
  };

  //-------End Function-------//

  //-------Variable-------//

  const hotelDisplayValue = storageCalibration.hotelCalibrations.map(
    (hotelCalibration, index) => ({
      display:
        EFootprint[hotelCalibration.footprint] + " " + hotelCalibration.number,
      value: index,
      disabled: false,
    })
  );

  const hotelFineTuningResult = carrierFineTuningResult[selectedHotelIndex];

  //-------End Variable-------//

  return (
    <div className="auto-calibration-with-fine-tuning-result-container">
      <div className="auto-calibration-with-fine-tuning-result-hotel-label">
        <label>Selected Hotel</label>
      </div>
      <div className="auto-calibration-with-fine-tuning-result-hotel-select">
        <CommonDropDown<number>
          value={selectedHotelIndex}
          id="hotel-select"
          onChange={(value) => {
            onHotelChanged(value);
          }}
          style={{
            width: "100%",
            height: 32,
          }}
          dropDownStyle={{
            maxHeight: 125,
          }}
          dropDownDisplayValues={hotelDisplayValue}
        />
      </div>
      <div className="auto-calibration-with-fine-tuning-result-content">
        <table className="auto-calibration-with-fine-tuning-result-table">
          <thead>
            <tr>
              <th>Position</th>
              <th>Result</th>
              <th>X</th>
              <th>Y</th>
              <th>Z</th>
              <th colSpan={2}>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr
              onClick={() => onClickRow(ECarrierCorner.Top)}
              className={
                selectedCarrierCorner === ECarrierCorner.Top ? "selected" : ""
              }
            >
              <td rowSpan={3}>Top</td>
              <td>Current</td>
              <td>
                {getTextBox(
                  "topUpdated",
                  "x",
                  hotelFineTuningResult.topUpdated.x
                )}
              </td>
              <td>
                {getTextBox(
                  "topUpdated",
                  "y",
                  hotelFineTuningResult.topUpdated.y
                )}
              </td>
              <td>
                {getTextBox(
                  "topUpdated",
                  "z",
                  hotelFineTuningResult.topUpdated.z
                )}
              </td>
              <td>
                <MtiButton //top, move to current position
                  variant="secondary"
                  colorVariant="alternate"
                  size="small"
                  onClick={() => onClickMove(ECarrierCorner.Top)}
                  disabled={
                    disabled || selectedCarrierCorner !== ECarrierCorner.Top
                  }
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M8 15.5C12.1422 15.5 15.5 12.1421 15.5 8C15.5 3.85787 12.1422 0.5 8 0.5C3.85785 0.5 0.5 3.85787 0.5 8C0.5 12.1421 3.85785 15.5 8 15.5ZM11.75 8L5.75 4.25V11.75L11.75 8Z"
                      fill="#0059CC"
                    />
                  </svg>
                </MtiButton>
              </td>
              <td>
                <MtiButton //top, input current position
                  variant="secondary"
                  colorVariant="alternate"
                  size="small"
                  onClick={() => onClickInputPos(ECarrierCorner.Top)}
                  disabled={
                    disabled || selectedCarrierCorner !== ECarrierCorner.Top
                  }
                >
                  <svg
                    width="20"
                    height="16"
                    viewBox="0 0 20 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M7.04348 0H0V16H7.04348V14.6087H1.3913V1.3913H7.04348V0ZM13 7H20V9H13L13.5 13L8 8L13.5 3L13 7Z"
                      fill="#0059CC"
                    />
                  </svg>
                </MtiButton>
              </td>
            </tr>
            <tr
              onClick={() => onClickRow(ECarrierCorner.Top)}
              className={
                selectedCarrierCorner === ECarrierCorner.Top ? "selected" : ""
              }
            >
              <td>Offset</td>
              <td>
                <label>{hotelFineTuningResult.topOffset.x}</label>
              </td>
              <td>
                {getOffsetTextBox(
                  "topOffset",
                  "y",
                  hotelFineTuningResult.topOffset.y
                )}
              </td>
              <td>
                {getOffsetTextBox(
                  "topOffset",
                  "z",
                  hotelFineTuningResult.topOffset.z
                )}
              </td>
              <td>
                <MtiButton //top, move to cursor position
                  variant="secondary"
                  colorVariant="alternate"
                  size="small"
                  onClick={() => onClickMoveCursor(ECarrierCorner.Top)}
                  disabled={
                    disabled || selectedCarrierCorner !== ECarrierCorner.Top
                  }
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M5.8011 13.1353C5.79327 13.1274 5.78284 13.1221 5.77762 13.1169C5.77241 13.1116 5.75937 13.1037 5.75154 13.0985L5.58201 12.8647C5.52202 12.7675 5.47247 12.6886 5.4203 12.5888C5.08906 11.9609 4.82825 11.3068 4.77869 10.5554L3.00513 10.5738C3.21378 13.9418 6.00714 16.7423 9.31171 16.9971V15.2054C8.13542 15.1082 7.26429 14.5723 6.44793 13.8892C6.33577 13.7947 5.83761 13.306 5.8011 13.1353Z"
                      fill="#0059CC"
                    />
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M3 9.42596H4.77356C4.92484 7.64739 6.08809 6.079 7.54606 5.34077C8.08335 5.06755 8.62846 4.8784 9.30658 4.7917C9.30919 4.51323 9.30658 3.32576 9.30658 3C8.03118 3.0972 6.82881 3.60161 5.85596 4.29518C4.36147 5.35916 3.10954 7.34265 3 9.42596Z"
                      fill="#0059CC"
                    />
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M6.92017 9.42586H9.31187L9.30665 6.95898C8.15123 7.16127 7.0897 8.21738 6.92017 9.42586Z"
                      fill="#0059CC"
                    />
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M9.31202 13.0355V10.5713L6.92554 10.5739C7.06377 11.2649 7.38197 11.7955 7.72364 12.166C8.01576 12.4838 8.67041 12.9331 9.31202 13.0355Z"
                      fill="#0059CC"
                    />
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M14.1966 13.138C14.2045 13.1328 14.2175 13.1249 14.2227 13.1196C14.2279 13.117 14.241 13.1065 14.2462 13.1012L14.4183 12.8674C14.4757 12.7728 14.5253 12.694 14.5801 12.5942C14.9087 11.9663 15.1695 11.3095 15.2191 10.5608L16.9952 10.5766C16.784 13.9445 13.9906 16.7451 10.6886 16.9999L10.686 15.2082C11.8623 15.111 12.7335 14.575 13.5524 13.892C13.6646 13.7974 14.1601 13.3088 14.1966 13.138Z"
                      fill="#0059CC"
                    />
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M16.9998 9.4284H15.2236C15.0724 7.64983 13.9117 6.08406 12.4537 5.34321C11.9165 5.06999 11.3687 4.88084 10.6906 4.79414C10.688 4.5183 10.6906 3.32821 10.6906 3.00244C11.9686 3.10227 13.1684 3.60668 14.1412 4.30024C15.6357 5.36423 16.8877 7.34509 16.9998 9.4284Z"
                      fill="#0059CC"
                    />
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M13.0778 9.4283H10.6887L10.6913 6.96143C11.8468 7.16634 12.9083 8.22245 13.0778 9.4283Z"
                      fill="#0059CC"
                    />
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M10.686 13.0409V10.5767H13.0751C12.9343 11.2702 12.6187 11.7983 12.2744 12.1713C11.9823 12.4892 11.3276 12.9358 10.686 13.0409Z"
                      fill="#0059CC"
                    />
                  </svg>
                </MtiButton>
              </td>
              <td>
                <MtiButton //top, input cursor position
                  variant="secondary"
                  colorVariant="alternate"
                  size="small"
                  onClick={() => onClickInputCursorPos(ECarrierCorner.Top)}
                  disabled={
                    disabled || selectedCarrierCorner !== ECarrierCorner.Top
                  }
                >
                  <svg
                    width="20"
                    height="16"
                    viewBox="0 0 20 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M7.04348 0H0V16H7.04348V14.6087H1.3913V1.3913H7.04348V0ZM13 7H20V9H13L13.5 13L8 8L13.5 3L13 7Z"
                      fill="#0059CC"
                    />
                  </svg>
                </MtiButton>
              </td>
            </tr>
            <tr
              onClick={() => onClickRow(ECarrierCorner.Top)}
              className={
                selectedCarrierCorner === ECarrierCorner.Top ? "selected" : ""
              }
            >
              <td>Original</td>
              <td>
                <label>{hotelFineTuningResult.topCurrent.x.toFixed(2)}</label>
              </td>
              <td>
                <label>{hotelFineTuningResult.topCurrent.y.toFixed(2)}</label>
              </td>
              <td>
                <label>{hotelFineTuningResult.topCurrent.z.toFixed(2)}</label>
              </td>
              <td></td>
              <td></td>
            </tr>
            <tr
              onClick={() => onClickRow(ECarrierCorner.Bottom)}
              className={
                selectedCarrierCorner === ECarrierCorner.Bottom
                  ? "selected"
                  : "bottom"
              }
            >
              <td rowSpan={3}>Bottom</td>
              <td>Current</td>
              <td>
                {getTextBox(
                  "bottomUpdated",
                  "x",
                  hotelFineTuningResult.bottomUpdated.x
                )}
              </td>
              <td>
                {getTextBox(
                  "bottomUpdated",
                  "y",
                  hotelFineTuningResult.bottomUpdated.y
                )}
              </td>
              <td>
                {getTextBox(
                  "bottomUpdated",
                  "z",
                  hotelFineTuningResult.bottomUpdated.z
                )}
              </td>
              <td>
                <MtiButton //bottom, move to current position
                  variant="secondary"
                  colorVariant="alternate"
                  size="small"
                  onClick={() => onClickMove(ECarrierCorner.Bottom)}
                  disabled={
                    disabled || selectedCarrierCorner !== ECarrierCorner.Bottom
                  }
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M8 15.5C12.1422 15.5 15.5 12.1421 15.5 8C15.5 3.85787 12.1422 0.5 8 0.5C3.85785 0.5 0.5 3.85787 0.5 8C0.5 12.1421 3.85785 15.5 8 15.5ZM11.75 8L5.75 4.25V11.75L11.75 8Z"
                      fill="#0059CC"
                    />
                  </svg>
                </MtiButton>
              </td>
              <td>
                <MtiButton //bottom, input current position
                  variant="secondary"
                  colorVariant="alternate"
                  size="small"
                  onClick={() => onClickInputPos(ECarrierCorner.Bottom)}
                  disabled={
                    disabled || selectedCarrierCorner !== ECarrierCorner.Bottom
                  }
                >
                  <svg
                    width="20"
                    height="16"
                    viewBox="0 0 20 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M7.04348 0H0V16H7.04348V14.6087H1.3913V1.3913H7.04348V0ZM13 7H20V9H13L13.5 13L8 8L13.5 3L13 7Z"
                      fill="#0059CC"
                    />
                  </svg>
                </MtiButton>
              </td>
            </tr>
            <tr
              onClick={() => onClickRow(ECarrierCorner.Bottom)}
              className={
                selectedCarrierCorner === ECarrierCorner.Bottom
                  ? "selected"
                  : " bottom"
              }
            >
              <td>Offset</td>
              <td>
                <label>{hotelFineTuningResult.bottomOffset.x}</label>
              </td>
              <td>
                {getOffsetTextBox(
                  "bottomOffset",
                  "y",
                  hotelFineTuningResult.bottomOffset.y
                )}
              </td>
              <td>
                {getOffsetTextBox(
                  "bottomOffset",
                  "z",
                  hotelFineTuningResult.bottomOffset.z
                )}
              </td>
              <td>
                <MtiButton //bottom, move to current position
                  variant="secondary"
                  colorVariant="alternate"
                  size="small"
                  onClick={() => onClickMoveCursor(ECarrierCorner.Bottom)}
                  disabled={
                    disabled || selectedCarrierCorner !== ECarrierCorner.Bottom
                  }
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M5.8011 13.1353C5.79327 13.1274 5.78284 13.1221 5.77762 13.1169C5.77241 13.1116 5.75937 13.1037 5.75154 13.0985L5.58201 12.8647C5.52202 12.7675 5.47247 12.6886 5.4203 12.5888C5.08906 11.9609 4.82825 11.3068 4.77869 10.5554L3.00513 10.5738C3.21378 13.9418 6.00714 16.7423 9.31171 16.9971V15.2054C8.13542 15.1082 7.26429 14.5723 6.44793 13.8892C6.33577 13.7947 5.83761 13.306 5.8011 13.1353Z"
                      fill="#0059CC"
                    />
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M3 9.42596H4.77356C4.92484 7.64739 6.08809 6.079 7.54606 5.34077C8.08335 5.06755 8.62846 4.8784 9.30658 4.7917C9.30919 4.51323 9.30658 3.32576 9.30658 3C8.03118 3.0972 6.82881 3.60161 5.85596 4.29518C4.36147 5.35916 3.10954 7.34265 3 9.42596Z"
                      fill="#0059CC"
                    />
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M6.92017 9.42586H9.31187L9.30665 6.95898C8.15123 7.16127 7.0897 8.21738 6.92017 9.42586Z"
                      fill="#0059CC"
                    />
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M9.31202 13.0355V10.5713L6.92554 10.5739C7.06377 11.2649 7.38197 11.7955 7.72364 12.166C8.01576 12.4838 8.67041 12.9331 9.31202 13.0355Z"
                      fill="#0059CC"
                    />
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M14.1966 13.138C14.2045 13.1328 14.2175 13.1249 14.2227 13.1196C14.2279 13.117 14.241 13.1065 14.2462 13.1012L14.4183 12.8674C14.4757 12.7728 14.5253 12.694 14.5801 12.5942C14.9087 11.9663 15.1695 11.3095 15.2191 10.5608L16.9952 10.5766C16.784 13.9445 13.9906 16.7451 10.6886 16.9999L10.686 15.2082C11.8623 15.111 12.7335 14.575 13.5524 13.892C13.6646 13.7974 14.1601 13.3088 14.1966 13.138Z"
                      fill="#0059CC"
                    />
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M16.9998 9.4284H15.2236C15.0724 7.64983 13.9117 6.08406 12.4537 5.34321C11.9165 5.06999 11.3687 4.88084 10.6906 4.79414C10.688 4.5183 10.6906 3.32821 10.6906 3.00244C11.9686 3.10227 13.1684 3.60668 14.1412 4.30024C15.6357 5.36423 16.8877 7.34509 16.9998 9.4284Z"
                      fill="#0059CC"
                    />
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M13.0778 9.4283H10.6887L10.6913 6.96143C11.8468 7.16634 12.9083 8.22245 13.0778 9.4283Z"
                      fill="#0059CC"
                    />
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M10.686 13.0409V10.5767H13.0751C12.9343 11.2702 12.6187 11.7983 12.2744 12.1713C11.9823 12.4892 11.3276 12.9358 10.686 13.0409Z"
                      fill="#0059CC"
                    />
                  </svg>
                </MtiButton>
              </td>
              <td>
                <MtiButton //bottom, input cursor position
                  variant="secondary"
                  colorVariant="alternate"
                  size="small"
                  onClick={() => onClickInputCursorPos(ECarrierCorner.Bottom)}
                  disabled={
                    disabled || selectedCarrierCorner !== ECarrierCorner.Bottom
                  }
                >
                  <svg
                    width="20"
                    height="16"
                    viewBox="0 0 20 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M7.04348 0H0V16H7.04348V14.6087H1.3913V1.3913H7.04348V0ZM13 7H20V9H13L13.5 13L8 8L13.5 3L13 7Z"
                      fill="#0059CC"
                    />
                  </svg>
                </MtiButton>
              </td>
            </tr>
            <tr
              onClick={() => onClickRow(ECarrierCorner.Bottom)}
              className={
                selectedCarrierCorner === ECarrierCorner.Bottom
                  ? "selected"
                  : "bottom"
              }
            >
              <td>Original</td>
              <td>
                <label>
                  {hotelFineTuningResult.bottomCurrent.x.toFixed(2)}
                </label>
              </td>
              <td>
                <label>
                  {hotelFineTuningResult.bottomCurrent.y.toFixed(2)}
                </label>
              </td>
              <td>
                <label>
                  {hotelFineTuningResult.bottomCurrent.z.toFixed(2)}
                </label>
              </td>
              <td></td>
              <td></td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}
